import React from "react";
import { useTranslation } from "react-i18next";
import { getTokens, getVariableValue, View } from "tamagui";
import { ImageFit } from "../../../ui/ImageFit";
import { Title } from "../../../ui/Title";
import type { BusinessFeedItem } from "../BusinessFeedItem";
import { FeedItemAuthorUI } from "../ui/FeedItemAuthor";
import { FeedTextInput } from "../ui/FeedTextInput";
import { FeedTextWithMentions } from "../ui/FeedTextWithMentions";

export type FeedItemShoutOutData = {
  authorUid: string;
  text: string;
  mediaUrl: string | null;
};

export const FeedItemShoutoutUI = ({
  item,
  isEditMode,
  onEdit,
}: {
  item: BusinessFeedItem;
  isEditMode: boolean;
  onEdit: () => void;
}) => {
  const itemData = item.data.itemData as FeedItemShoutOutData;
  const tokens = getTokens();
  const larger = getVariableValue(tokens.space.$larger);
  const { t } = useTranslation();
  const author = item.maybeAuthor;

  if (!author) {
    return null;
  }

  const hasTextContent = itemData.text || isEditMode;

  return (
    <>
      <FeedItemAuthorUI
        businessUid={item.businessPlan.uid}
        feedItem={item}
        author={author}
        dateTs={item.data.ts}
        paddingLeft={larger - 5}
      />
      <View>
        {itemData.mediaUrl && (
          <View paddingTop="$medium">
            <ImageFit assetUrl={itemData.mediaUrl} />
          </View>
        )}
        {hasTextContent && (
          <View
            paddingVertical={"$large"}
            paddingHorizontal={"$space.larger"}
            paddingLeft={larger - 5}
          >
            {!isEditMode && (
              <Title $xs={{ fontSize: 18, lineHeight: 24 }}>
                <FeedTextWithMentions
                  text={`📢 ${itemData.text}`}
                  mentions={item.data.mentions}
                  businessPlan={item.businessPlan}
                />
              </Title>
            )}

            {isEditMode && (
              <FeedTextInput
                autoFocus
                businessPlan={item.businessPlan}
                placeholder={t("business:feed_edit_item")}
                placeholderFocused={t("business:feed_mention_tip")}
                defaultValue={itemData.text}
                submitOnEnter
                onSubmitEditing={async (e) => {
                  await item.edit(e.nativeEvent.text);
                  onEdit();
                }}
              />
            )}
          </View>
        )}
      </View>
    </>
  );
};
